import { useEffect, useState, useCallback, useRef } from 'react';
import { useSocket } from './use-socket';
import { useCollaborationStore } from '@/stores/collaboration-store';
import type { CollaborationUser, CursorPosition } from '@/types/collaboration';
import { isFeatureEnabled } from '@/config/features';
import { throttle } from '@/lib/utils';

interface UseDatabaseCollaborationOptions {
  databaseId: string | undefined;
  user: {
    id: string;
    name: string;
    email: string;
    image: string | null;
  } | null;
  enabled?: boolean;
}

interface QueryExecution {
  userId: string;
  userName: string;
  query: string;
  executionTime: number;
  timestamp: number;
}

interface UserCursor {
  userId: string;
  userName: string;
  userImage: string | null;
  position: CursorPosition;
  lastUpdate: number;
}

interface UseDatabaseCollaborationReturn {
  activeUsers: CollaborationUser[];
  isConnected: boolean;
  isLoading: boolean;
  error: Error | null;
  totalUsers: number;
  reconnect: () => void;
  broadcastQueryExecution: (query: string, executionTime: number) => void;
  onQueryExecuted: (callback: (execution: QueryExecution) => void) => () => void;
  broadcastCursorMove: (position: CursorPosition) => void;
  activeCursors: UserCursor[];
}

export function useDatabaseCollaboration({
  databaseId,
  user,
  enabled = true,
}: UseDatabaseCollaborationOptions): UseDatabaseCollaborationReturn {
  const { socket, isConnected } = useSocket();
  const { users, setUsers, cursors, updateCursor, removeCursor } = useCollaborationStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const hasJoinedRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();

  // Get active users for current database
  const activeUsers = databaseId ? users[databaseId] || [] : [];

  // Get active cursors for current database (excluding current user)
  const activeCursors: UserCursor[] = databaseId
    ? (cursors[databaseId] || [])
        .filter(cursor => cursor.userId !== user?.id)
        .map(cursor => {
          const cursorUser = activeUsers.find(u => u.id === cursor.userId);
          return {
            userId: cursor.userId,
            userName: cursorUser?.name || 'Unknown',
            userImage: cursorUser?.image || null,
            position: cursor.position,
            lastUpdate: cursor.lastUpdate,
          };
        })
    : [];

  // Join database room
  const joinDatabase = useCallback(() => {
    // Check feature flag
    if (!isFeatureEnabled('presenceIndicators')) {
      setIsLoading(false);
      return;
    }

    if (!socket || !isConnected || !databaseId || !user || !enabled) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate joins
    if (hasJoinedRef.current) return;

    try {
      socket.emit('join-database', {
        databaseId,
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          image: user.image,
          socketId: socket.id,
        },
      });

      hasJoinedRef.current = true;
      setIsLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to join database'));
      setIsLoading(false);
    }
  }, [socket, isConnected, databaseId, user, enabled]);

  // Leave database room
  const leaveDatabase = useCallback(() => {
    if (!socket || !databaseId || !user || !hasJoinedRef.current) return;

    try {
      socket.emit('leave-database', {
        databaseId,
        userId: user.id,
      });

      hasJoinedRef.current = false;
    } catch (err) {
      console.error('Failed to leave database:', err);
    }
  }, [socket, databaseId, user]);

  // Reconnect handler
  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    hasJoinedRef.current = false;
    setIsLoading(true);

    reconnectTimeoutRef.current = setTimeout(() => {
      joinDatabase();
    }, 1000);
  }, [joinDatabase]);

  // Setup socket event listeners
  useEffect(() => {
    if (!socket || !isConnected || !databaseId || !enabled) return;

    const handleUsersUpdate = (data: { databaseId: string; users: CollaborationUser[] }) => {
      if (data.databaseId === databaseId) {
        setUsers(databaseId, data.users);
      }
    };

    const handleUserJoined = (data: { databaseId: string; user: CollaborationUser }) => {
      if (data.databaseId === databaseId) {
        const currentUsers = users[databaseId] || [];
        // Avoid duplicates
        if (!currentUsers.some(u => u.id === data.user.id)) {
          setUsers(databaseId, [...currentUsers, data.user]);
        }
      }
    };

    const handleUserLeft = (data: { databaseId: string; userId: string }) => {
      if (data.databaseId === databaseId) {
        const currentUsers = users[databaseId] || [];
        setUsers(databaseId, currentUsers.filter(u => u.id !== data.userId));
      }
    };

    const handleReconnect = () => {
      reconnect();
    };

    const handleDisconnect = () => {
      setError(new Error('Connection lost'));
    };

    socket.on('users-update', handleUsersUpdate);
    socket.on('user-joined', handleUserJoined);
    socket.on('user-left', handleUserLeft);
    socket.on('reconnect', handleReconnect);
    socket.on('disconnect', handleDisconnect);

    return () => {
      socket.off('users-update', handleUsersUpdate);
      socket.off('user-joined', handleUserJoined);
      socket.off('user-left', handleUserLeft);
      socket.off('reconnect', handleReconnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket, isConnected, databaseId, enabled, users, setUsers, reconnect]);

  // Join on mount, leave on unmount
  useEffect(() => {
    if (!enabled || !databaseId || !user) {
      setIsLoading(false);
      return;
    }

    joinDatabase();

    // Cleanup: leave room when unmounting or database changes
    return () => {
      leaveDatabase();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [databaseId, user, enabled, joinDatabase, leaveDatabase]);

  // Handle visibility change - rejoin when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && enabled && databaseId && user) {
        // Rejoin if we were disconnected while tab was hidden
        if (!hasJoinedRef.current && isConnected) {
          reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled, databaseId, user, isConnected, reconnect]);

  // Broadcast query execution to other users
  const broadcastQueryExecution = useCallback((query: string, executionTime: number) => {
    if (!socket || !isConnected || !databaseId || !user) return;

    socket.emit('query-execute', {
      databaseId,
      userId: user.id,
      userName: user.name,
      query,
      executionTime,
      timestamp: Date.now(),
    });
  }, [socket, isConnected, databaseId, user]);

  // Subscribe to query execution events from other users
  const onQueryExecuted = useCallback((callback: (execution: QueryExecution) => void) => {
    if (!socket) return () => {};

    const handler = (data: QueryExecution) => {
      // Only trigger for other users, not yourself
      if (data.userId !== user?.id) {
        callback(data);
      }
    };

    socket.on('query-executed', handler);

    return () => {
      socket.off('query-executed', handler);
    };
  }, [socket, user]);

  // Broadcast cursor movement (throttled to max 60fps)
  const broadcastCursorMove = useCallback(
    throttle((position: CursorPosition) => {
      if (!socket || !isConnected || !databaseId || !user) return;
      if (!isFeatureEnabled('cursors')) return;

      socket.emit('cursor-move', {
        databaseId,
        userId: user.id,
        position,
      });
    }, 16), // 16ms = ~60fps
    [socket, isConnected, databaseId, user]
  );

  // Listen for cursor movements from other users
  useEffect(() => {
    if (!socket || !isConnected || !databaseId || !enabled) return;
    if (!isFeatureEnabled('cursors')) return;

    const handleCursorMove = (data: { databaseId: string; userId: string; position: CursorPosition }) => {
      if (data.databaseId === databaseId && data.userId !== user?.id) {
        updateCursor(databaseId, data.userId, data.position);
      }
    };

    const handleUserLeft = (data: { databaseId: string; userId: string }) => {
      if (data.databaseId === databaseId) {
        removeCursor(databaseId, data.userId);
      }
    };

    socket.on('cursor-move', handleCursorMove);
    socket.on('user-left', handleUserLeft);

    return () => {
      socket.off('cursor-move', handleCursorMove);
      socket.off('user-left', handleUserLeft);
    };
  }, [socket, isConnected, databaseId, enabled, user, updateCursor, removeCursor]);

  return {
    activeUsers,
    isConnected,
    isLoading,
    error,
    totalUsers: activeUsers.length,
    reconnect,
    broadcastQueryExecution,
    onQueryExecuted,
    broadcastCursorMove,
    activeCursors,
  };
}
